import React, { useEffect, useState } from 'react';
import { Table, Form, Alert, Card, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getUserLogs } from '../../../services/UserLogsService';
import { isAdmin } from '../../../services/AuthService';
import MainPagetitle from '../../layouts/MainPagetitle';

export default function UserLogs() {
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [allEntityTypes, setAllEntityTypes] = useState([]);
    const [allActions, setAllActions] = useState([]);
    const [filters, setFilters] = useState({
        entity_type: '',
        action: '',
        date_from: '',
        date_to: '',
    });

    const load = async () => {
        if (!isAdmin()) return;
        setLoading(true);
        setError('');
        try {
            const params = {};
            if (filters.entity_type) params.entity_type = filters.entity_type;
            if (filters.action) params.action = filters.action;
            if (filters.date_from) params.date_from = filters.date_from;
            if (filters.date_to) params.date_to = filters.date_to;
            params.limit = 1000; // Get last 1000 logs
            
            const res = await getUserLogs(params);
            setLogs(res.data || []);
        } catch (err) {
            setError(err.response?.data?.error || t('userLogs.errors.load'));
        } finally {
            setLoading(false);
        }
    };

    // Load all distinct entity types and actions once (unfiltered) for dropdown options
    const loadFilterOptions = async () => {
        if (!isAdmin()) return;
        try {
            const params = { limit: 1000 };
            const res = await getUserLogs(params);
            const data = res.data || [];
            const types = [...new Set(data.map(log => log.entity_type).filter(Boolean))].sort();
            const acts = [...new Set(data.map(log => log.action).filter(Boolean))].sort();
            setAllEntityTypes(types);
            setAllActions(acts);
        } catch {
            // Non-critical; dropdowns will fall back to current logs
        }
    };

    useEffect(() => {
        load();
    }, [filters]);

    useEffect(() => {
        loadFilterOptions();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const formatAction = (action) => {
        return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getActionBadgeVariant = (action) => {
        if (action.includes('create') || action.includes('add')) return 'success';
        if (action.includes('update') || action.includes('edit')) return 'primary';
        if (action.includes('delete') || action.includes('remove')) return 'danger';
        return 'secondary';
    };

    const getEntityTypeBadgeVariant = (entityType) => {
        const variants = {
            'order': 'info',
            'material': 'warning',
            'recipe': 'success',
            'product': 'primary',
            'user': 'danger',
            'material_stock': 'secondary',
        };
        return variants[entityType] || 'secondary';
    };

    const parseDetails = (detailsJson) => {
        if (!detailsJson) return null;
        try {
            return JSON.parse(detailsJson);
        } catch {
            return null;
        }
    };

    if (!isAdmin()) {
        return (
            <div className="container-fluid">
                <MainPagetitle title={t('userLogs.pageTitle')} />
                <Alert variant="danger">{t('userLogs.accessDenied')}</Alert>
            </div>
        );
    }

    // Use preloaded list so dropdown always shows all options, not just current result set
    const entityTypes = allEntityTypes.length > 0 ? allEntityTypes : [...new Set(logs.map(log => log.entity_type).filter(Boolean))].sort();
    const actions = allActions.length > 0 ? allActions : [...new Set(logs.map(log => log.action).filter(Boolean))].sort();

    return (
        <div className="container-fluid">
            <MainPagetitle title={t('userLogs.pageTitle')} />
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header>
                            <h4 className="card-title mb-0">{t('userLogs.cardTitle')}</h4>
                            <small className="text-muted">{t('userLogs.subtitle')}</small>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                            {/* Filters */}
                            <div className="row mb-3">
                                <div className="col-md-3">
                                    <Form.Label>{t('userLogs.filters.entityType')}</Form.Label>
                                    <Form.Select
                                        value={filters.entity_type}
                                        onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
                                    >
                                        <option value="">{t('userLogs.filters.allTypes')}</option>
                                        {entityTypes.map(type => (
                                            <option key={type} value={type}>{type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <div className="col-md-3">
                                    <Form.Label>{t('userLogs.filters.action')}</Form.Label>
                                    <Form.Select
                                        value={filters.action}
                                        onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                                    >
                                        <option value="">{t('userLogs.filters.allActions')}</option>
                                        {actions.map(action => (
                                            <option key={action} value={action}>{formatAction(action)}</option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <div className="col-md-2">
                                    <Form.Label>{t('userLogs.filters.dateFrom')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filters.date_from}
                                        onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <Form.Label>{t('userLogs.filters.dateTo')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filters.date_to}
                                        onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-2 d-flex align-items-end">
                                    <div className="text-muted w-100">
                                        {t('userLogs.showingLogs', { count: logs.length })}
                                    </div>
                                </div>
                            </div>
                            {(filters.date_from || filters.date_to) && (
                                <div className="mb-2">
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setFilters({ ...filters, date_from: '', date_to: '' })}
                                    >
                                        {t('userLogs.filters.clearDates')}
                                    </button>
                                </div>
                            )}

                            {loading ? (
                                <p>{t('common.loading')}</p>
                            ) : logs.length === 0 ? (
                                <Alert variant="info">{t('userLogs.noLogs')}</Alert>
                            ) : (
                                <div className="table-responsive">
                                    <Table responsive hover className="user-logs-table">
                                        <thead>
                                            <tr>
                                                <th>{t('userLogs.columns.timestamp')}</th>
                                                <th>{t('userLogs.columns.user')}</th>
                                                <th>{t('userLogs.columns.action')}</th>
                                                <th>{t('userLogs.columns.entityType')}</th>
                                                <th>{t('userLogs.columns.entity')}</th>
                                                <th>{t('userLogs.columns.details')}</th>
                                                <th>{t('userLogs.columns.ipAddress')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.map((log) => {
                                                const details = parseDetails(log.details);
                                                return (
                                                    <tr key={log.id}>
                                                        <td className="align-middle">
                                                            <small>{formatDate(log.created_at)}</small>
                                                        </td>
                                                        <td className="align-middle">
                                                            <strong>{log.username}</strong>
                                                        </td>
                                                        <td className="align-middle">
                                                            <Badge bg={getActionBadgeVariant(log.action)}>
                                                                {formatAction(log.action)}
                                                            </Badge>
                                                        </td>
                                                        <td className="align-middle">
                                                            <Badge bg={getEntityTypeBadgeVariant(log.entity_type)}>
                                                                {log.entity_type.replace(/_/g, ' ')}
                                                            </Badge>
                                                        </td>
                                                        <td className="align-middle">
                                                            {log.entity_name ? (
                                                                <span>
                                                                    {log.entity_name}
                                                                    {log.entity_id && <small className="text-muted"> (ID: {log.entity_id})</small>}
                                                                </span>
                                                            ) : (
                                                                log.entity_id ? `ID: ${log.entity_id}` : '-'
                                                            )}
                                                        </td>
                                                        <td className="align-middle">
                                                            {details ? (
                                                                <small className="text-muted">
                                                                    {Object.entries(details).slice(0, 3).map(([key, value]) => (
                                                                        <div key={key}>
                                                                            <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                                        </div>
                                                                    ))}
                                                                    {Object.keys(details).length > 3 && (
                                                                        <div className="text-muted">...</div>
                                                                    )}
                                                                </small>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td className="align-middle">
                                                            <small className="text-muted">{log.ip_address || '-'}</small>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
}
